import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Check } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { gradientThemes, GradientTheme } from './gradientThemes';

interface GradientThemeSelectorProps {
  value?: string;
  onChange: (theme: GradientTheme) => void;
  label?: string;
}

const GradientThemeSelector: React.FC<GradientThemeSelectorProps> = ({
  value,
  onChange,
  label,
}) => {
  const translate = useTranslate();
  const currentLocale = translate('language', { _: 'en' });
  const isZhTW = currentLocale === 'zh-TW';

  return (
    <Box>
      {label && (
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {gradientThemes.map((theme) => (
          <Box key={theme.id}>
            <Paper
              elevation={value === theme.id ? 4 : 1}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                overflow: 'hidden',
                borderRadius: 2,
                border: value === theme.id ? '2px solid #00C853' : '2px solid transparent',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
              onClick={() => onChange(theme)}
            >
              {/* Gradient Preview */}
              <Box
                sx={{
                  height: 80,
                  background: `linear-gradient(${theme.angle}deg, ${theme.startColor}, ${theme.endColor})`,
                }}
              />

              {/* Theme Name */}
              <Box sx={{ p: 1.5, backgroundColor: '#fff' }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: value === theme.id ? 600 : 400,
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  {isZhTW ? theme.nameZhTW : theme.name}
                </Typography>
              </Box>

              {/* Selected Indicator */}
              {value === theme.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  <Check sx={{ fontSize: 16, color: '#00C853' }} />
                </Box>
              )}
            </Paper>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default GradientThemeSelector;